import dayjs from "dayjs";

import type { RequestDetailResponse, RequestListItem } from "@synthesis/shared-services/external/application/types";
import type { InternalRequestDetailResponse } from "@synthesis/shared-services/internal/application/types";

type ApplicationEntity = RequestListItem | RequestDetailResponse | InternalRequestDetailResponse;

export type PersonNameFields = {
  lastName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
};

const hasValue = (value: unknown): boolean => value !== undefined && value !== null;

const isObjectRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

export const formatPersonName = (person?: PersonNameFields | null): string => {
  const fullName = [person?.lastName, person?.firstName, person?.middleName].filter(Boolean).join(" ");

  return fullName || "—";
};

export const getPersonDisplay = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim()) {
    return value;
  }

  if (isObjectRecord(value)) {
    const fullName = formatPersonName(value as PersonNameFields);

    return fullName !== "—" ? fullName : undefined;
  }

  return undefined;
};

export const getInternalInitiatorDisplay = (application: Record<string, unknown>): string => {
  const legacyDisplay = application.initiatorDisplay;

  if (typeof legacyDisplay === "string" && legacyDisplay.trim()) {
    return legacyDisplay;
  }

  return getPersonDisplay(application.initiator) ?? "—";
};

/**
 * Internal заявки имеют поля, отсутствующие у external версии.
 */
export const isInternalApplication = (application: ApplicationEntity | null | undefined): application is InternalRequestDetailResponse => {
  const record = (application ?? {}) as Record<string, unknown>;

  return (
    hasValue(record.importedAt) ||
    hasValue(record.initiatorDisplay) ||
    hasValue(record.externalRequestId) ||
    hasValue(record.externalCreatedAt) ||
    hasValue(record.assignments) ||
    hasValue(record.files)
  );
};

/**
 * External заявки имеют отдельные поля карточки/инициатора.
 */
export const isExternalApplication = (application: ApplicationEntity | null | undefined): application is RequestListItem | RequestDetailResponse => {
  const record = (application ?? {}) as Record<string, unknown>;

  return (
    hasValue(record.createdAt) ||
    hasValue(record.initiatorFio) ||
    hasValue(record.operatorState) ||
    hasValue(record.submittedAt) ||
    (!isInternalApplication(application) && hasValue(record.applicant))
  );
};

export const formatImportedAt = (item: ApplicationEntity) => {
  const at = "importedAt" in item ? item.importedAt : "createdAt" in item ? item.createdAt : undefined;
  return at ? dayjs(at).format("DD.MM.YYYY, HH:mm") : "—";
};

export const formatApplicationNumber = (value: bigint | number | null | undefined) => (value != null ? `№${String(value)}` : "—");

export const formatInitiatorFio = (item: ApplicationEntity) => {
  const record = item as Record<string, unknown>;
  const initiatorFromApi = getPersonDisplay(record.initiator);

  if (initiatorFromApi) {
    return initiatorFromApi;
  }

  if ("initiatorFio" in item && item.initiatorFio) {
    return item.initiatorFio;
  }

  if (isInternalApplication(item)) {
    return getInternalInitiatorDisplay(record);
  }

  if ("applicant" in item) {
    return formatPersonName(item.applicant);
  }

  return "—";
};
